import React, { useState, FC } from 'react';
import styled, { css } from 'styled-components/macro';

import { ColorVariants } from 'theme';

const Wrapper = styled.div<{ $color: ColorVariants }>`
    color: ${({ theme, $color }) =>
        $color ? theme.colors[$color] : theme.colors.body};
`;

const Item = styled.div<{ $background: ColorVariants }>`
    background-color: ${({ theme, $background }) =>
        $background ? theme.colors[$background] : theme.colors.white};

    &:not(:first-child) {
        margin-top: ${({ theme }) => theme.space.tiny};
    }
`;

const Header = styled.header`
    position: relative;
    padding: ${({ theme }) => theme.space.base};
    padding-left: ${({ theme }) => theme.space.xlarge};
    cursor: pointer;
    user-select: none;
    outline: none;
`;

const Title = styled.p`
    font-size: 1.125rem;
    line-height: 120%;
    color: ${({ theme }) => theme.colors.darkGrey};
`;

const Icon = styled.span.attrs({ role: 'button' })<{ reversed: boolean }>`
    position: absolute;
    left: ${({ theme }) => theme.space.base};
    top: 50%;

    width: 1rem;
    height: 2px;

    background-color: ${({ theme }) => theme.colors.brand};

    &:after {
        position: absolute;
        left: 0.5rem;
        top: 1px;

        background-color: ${({ theme }) => theme.colors.brand};

        content: '';
        width: 2px;
        height: 1rem;

        transition: transform 0.2s ease-in-out;
        transform-origin: center;
        transform: translate(-50%, -50%);

        ${({ reversed }) =>
            reversed &&
            css`
                transform: translate(-50%, -50%) rotate(90deg);
            `};
    }
`;

const Body = styled.div`
    margin: 0;
    padding: 0 ${({ theme }) => theme.space.large};
    padding-left: ${({ theme }) => theme.space.xlarge};

    font-size: 1.125rem;
    line-height: 150%;
    color: ${({ theme }) => theme.colors.baseGray};

    overflow: hidden;
    transition: all 0.3s ease-out;
    height: 0;
`;

export interface AccordionProps {
    /**
     * Accordion item background color
     * */
    background?: ColorVariants;
    /**
     * Accordion text color
     * */
    color?: ColorVariants;
}

/**
 *  A basic accordion component, can be used for displaying
 * <strong>FAQs</strong> or any other long list of content.
 * Accordions should only be used on sections where the background color is not white.
 */
const Accordion: FC<AccordionProps> = ({
    background = 'toast',
    color = 'darkGrey',
    children,
}) => {
    const [actives, setActives] = useState<string[]>([]);

    const onClick = (i: number) => {
        const index = String(i);
        const element = document.querySelector(`.accordion__body-${index}`);

        if (element) {
            if (actives.indexOf(index) >= 0) {
                collapseSection(element as HTMLElement);
                setActives((old) => old.filter((i) => i !== index));
            } else {
                expandSection(element as HTMLElement);
                setActives((old) => [...old, index]);
            }
        }
    };

    return (
        <Wrapper $color={color}>
            {Array.isArray(children) &&
                children.map((c, index) => {
                    const child = c as React.ReactElement;
                    return (
                        child && (
                            <Item
                                key={`accordion-item-${index}`}
                                onClick={() => onClick(index)}
                                $background={background}
                            >
                                <Header role="button">
                                    <Icon
                                        reversed={actives.includes(
                                            String(index),
                                        )}
                                    />

                                    <Title
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                child.props.label ||
                                                child.props['data-label'],
                                        }}
                                    />
                                </Header>

                                <Body className={`accordion__body-${index}`}>
                                    {React.Children.map(
                                        child.props.children,
                                        (child) => child,
                                    )}
                                </Body>
                            </Item>
                        )
                    );
                })}
        </Wrapper>
    );
};

export default Accordion;

function collapseSection(element: HTMLElement) {
    const oldElementTransition = element.style.transition;
    element.style.transition = '';

    const sectionHeight = element.scrollHeight;

    requestAnimationFrame(function () {
        element.style.transition = oldElementTransition;
        element.style.height = `${sectionHeight}px`;

        requestAnimationFrame(function () {
            element.style.height = '0px';
            element.style.paddingBottom = '0px';
        });
    });
}

function expandSection(element: HTMLElement) {
    const sectionHeight = element.scrollHeight;
    element.style.height = sectionHeight + 'px';
    element.style.paddingBottom = '32px';

    element.addEventListener('transitionend', function _handle() {
        element.removeEventListener('transitionend', _handle);
    });
}
